import React, { Dispatch, FunctionComponent, useState } from "react";
import CustomError from "./CustomError";

/* Validation */
import { Formik, useFormikContext } from "formik";
import { validationSchema } from "./formConfig";

/* API */
import axios from "axios";
import { Endpoints } from "@internal/common";
import { ClaimReportGlassesFormData } from "./ClaimModels";
import FormAlert from "./components/FormAlert";
import { Title, Text, Flex, Button } from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";
import FormInputGroup from "./components/FormInputGroup";
import FormTextArea from "./components/FormTextArea";
import FormDatepicker from "./components/FormDatepicker";

export interface IProps {
  claimType: string;
}

interface FormData extends ClaimReportGlassesFormData {
  claimType: string;
}

const ClaimReportGlasses: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState<FormData>({
    claimType: props.claimType,
    cvrNumber: "",
    policyNumber: "",
    contactPersonName: "",
    email: "",
    contactPersonPhoneNumber: "",
    injuredPartyName: "",
    injuredPartyDOB: "",
    injuredPartyEmployment: "",
    injuredPartyAddress: "",
    isInjuredPartyEmployedAtReporter: "",
    injuryDate: "",
    injuryLocation: "",
    kindOfWorkWhenInjured: "",
    injuryReason: "",
    hadGlassesOn: "",
    glassesLocation: "",
    dateOfPurchase: "",
    originalPriceOfGlasses: "",
    priceEstimate: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    setIsLoading(true);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => {
        setIsLoading(false);
        setApiError(true);
      });
  };

  return (
    <>
      {submitted ? (
        <FormAlert
          severity="success"
          title="Tak for din anmeldelse"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse."
        />
      ) : (
        <>
          <Title size={5}>Anmeld skade på briller</Title>
          <Text>* Påkrævede felter</Text>
          <Formik
            enableReinitialize
            initialValues={{
              cvrNumber: formData.cvrNumber,
              policyNumber: formData.policyNumber,
              contactPersonName: formData.contactPersonName,
              email: formData.email,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              injuredPartyName: formData.injuredPartyName,
              injuredPartyDOB: formData.injuredPartyDOB,
              injuredPartyEmployment: formData.injuredPartyEmployment,
              injuredPartyAddress: formData.injuredPartyAddress,
              isInjuredPartyEmployedAtReporter:
                formData.isInjuredPartyEmployedAtReporter,
              injuryDate: formData.injuryDate,
              injuryLocation: formData.injuryLocation,
              kindOfWorkWhenInjured: formData.kindOfWorkWhenInjured,
              injuryReason: formData.injuryReason,
              hadGlassesOn: formData.hadGlassesOn,
              glassesLocation: formData.glassesLocation,
              dateOfPurchase: formData.dateOfPurchase,
              originalPriceOfGlasses: formData.originalPriceOfGlasses,
              priceEstimate: formData.priceEstimate,
              consent: formData.consent,
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            <RenderForm isLoading={isLoading} setFormData={setFormData} />
          </Formik>
        </>
      )}
      {apiError && (
        <FormAlert
          severity="error"
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
        />
      )}
    </>
  );
};

interface RenderProps {
  isLoading: boolean;
  setFormData: Dispatch<React.SetStateAction<FormData>>;
}

const RenderForm: FunctionComponent<RenderProps> = ({
  isLoading,
  setFormData,
}) => {
  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values: formData,
  } = useFormikContext<FormData>();

  const todaysDate = new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="cvrNumber"
          labelText="CVR-nummer *"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("cvrNumber", event.target.value);
            setFormData({
              ...formData,
              cvrNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.cvrNumber && errors.cvrNumber)}
          errorText={errors.cvrNumber}
          value={formData.cvrNumber}
        />
        <FormInput
          id="policy-number"
          labelText="Policenummer *"
          name="policyNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("policyNumber", event.target.value);
            setFormData({
              ...formData,
              policyNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.policyNumber && errors.policyNumber)}
          errorText={errors.policyNumber}
          value={formData.policyNumber}
        />
        <FormInput
          id="contactPersonName"
          labelText="Navn (kontaktperson) *"
          name="contactPersonName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonName", event.target.value);
            setFormData({
              ...formData,
              contactPersonName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.contactPersonName && errors.contactPersonName)}
          errorText={errors.contactPersonName}
          value={formData.contactPersonName}
        />
        <FormInput
          id="email"
          type={"email"}
          labelText="Email (kontaktperson) *"
          name="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
            setFormData({
              ...formData,
              email: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={formData.email}
        />
        <FormInput
          id="contactPersonPhoneNumber"
          labelText="Telefonnummer (kontaktperson) *"
          name="contactPersonPhoneNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              contactPersonPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.contactPersonPhoneNumber &&
              errors.contactPersonPhoneNumber
            )
          }
          errorText={errors.contactPersonPhoneNumber}
          value={formData.contactPersonPhoneNumber}
        />

        <Title size={5}>Om skaden</Title>

        <FormInput
          id="injuredPartyName"
          labelText="Skadelidtes fulde navn *"
          name="injuredPartyName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyName", event.target.value);
            setFormData({
              ...formData,
              injuredPartyName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.injuredPartyName && errors.injuredPartyName)}
          errorText={errors.injuredPartyName}
          value={formData.injuredPartyName}
        />
        <FormDatepicker
          labelText="Fødselsdato *"
          id="injuredPartyDOB"
          onChange={(event, value) => {
            setFormData({
              ...formData,
              injuredPartyDOB: event?.toDateString() ?? "",
            });
            setFieldValue("injuredPartyDOB", event?.toDateString() ?? "");
          }}
          invalid={!!(touched.injuredPartyDOB && errors.injuredPartyDOB)}
          errorText={errors.injuredPartyDOB}
          value={formData.injuredPartyDOB}
        />
        <FormInput
          id="injuredPartyEmployment"
          labelText="Skadelidtes stilling eller beskæftigelse *"
          name="injuredPartyEmployment"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyEmployment", event.target.value);
            setFormData({
              ...formData,
              injuredPartyEmployment: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.injuredPartyEmployment && errors.injuredPartyEmployment)
          }
          errorText={errors.injuredPartyEmployment}
          value={formData.injuredPartyEmployment}
        />
        <FormTextArea
          id="injuredPartyAddress"
          labelText="Skadelidtes adresse *"
          maxLength={500}
          placeholder="Oplys vejnavn og nummer, postnummer samt by."
          value={formData.injuredPartyAddress}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("injuredPartyAddress", event.target.value);
            setFormData({
              ...formData,
              injuredPartyAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.injuredPartyAddress && errors.injuredPartyAddress)
          }
          errorText={errors.injuredPartyAddress}
        />
        <FormInputGroup
          labelText="Er skadelidte ansat hos anmelderen? *"
          id="isInjuredPartyEmployedAtReporter"
          onChange={(event) => {
            setFormData({
              ...formData,
              isInjuredPartyEmployedAtReporter: event.at(0) ?? "",
            });
            setFieldValue(
              "isInjuredPartyEmployedAtReporter",
              event.at(0) ?? "",
            );
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.isInjuredPartyEmployedAtReporter}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.isInjuredPartyEmployedAtReporter &&
              errors.isInjuredPartyEmployedAtReporter
            )
          }
          errorText={errors.isInjuredPartyEmployedAtReporter}
        />
        <FormInput
          id="injuryDate"
          labelText="Hvornår skete skaden? *"
          helpText="Skriv gerne dato og klokkeslæt."
          name="injuryDate"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuryDate", event.target.value);
            setFormData({
              ...formData,
              injuryDate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.injuryDate && errors.injuryDate)}
          errorText={errors.injuryDate}
          value={formData.injuryDate}
        />
        <FormInput
          id="injuryLocation"
          labelText="Hvor skete skaden? *"
          name="injuryLocation"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuryLocation", event.target.value);
            setFormData({
              ...formData,
              injuryLocation: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.injuryLocation && errors.injuryLocation)}
          errorText={errors.injuryLocation}
          value={formData.injuryLocation}
        />
        <FormTextArea
          id="kindOfWorkWhenInjured"
          labelText="Hvilket arbejde var skadelidte beskæftiget med, da skaden skete?  *"
          maxLength={500}
          value={formData.kindOfWorkWhenInjured}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("kindOfWorkWhenInjured", event.target.value);
            setFormData({
              ...formData,
              kindOfWorkWhenInjured: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.kindOfWorkWhenInjured && errors.kindOfWorkWhenInjured)
          }
          errorText={errors.kindOfWorkWhenInjured}
        />
        <FormTextArea
          id="injuryReason"
          labelText="Hvad var årsagen til at brillen blev beskadiget?  *"
          maxLength={500}
          value={formData.injuryReason}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("injuryReason", event.target.value);
            setFormData({
              ...formData,
              injuryReason: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.injuryReason && errors.injuryReason)}
          errorText={errors.injuryReason}
        />
        <FormInputGroup
          labelText="Havde skadelidte brillen på, da skaden skete? *"
          id="hadGlassesOn"
          onChange={(event) => {
            setFormData({
              ...formData,
              hadGlassesOn: event.at(0) ?? "",
            });
            setFieldValue("hadGlassesOn", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.hadGlassesOn}
          onBlur={handleBlur}
          invalid={!!(touched.hadGlassesOn && errors.hadGlassesOn)}
          errorText={errors.hadGlassesOn}
        />
        {formData.hadGlassesOn === "Nej" && (
          <FormInput
            id="glassesLocation"
            labelText="Hvor var brillen henne? *"
            name="glassesLocation"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("glassesLocation", event.target.value);
              setFormData({
                ...formData,
                glassesLocation: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={!!(touched.glassesLocation && errors.glassesLocation)}
            errorText={errors.glassesLocation}
            value={formData.glassesLocation}
          />
        )}
        <FormDatepicker
          labelText="Hvornår blev den beskadigede brille købt? *"
          id="dateOfPurchase"
          onChange={(event, value) => {
            console.log("DateChange: ", event, value);
            setFormData({
              ...formData,
              dateOfPurchase: event?.toDateString() ?? "",
            });
            setFieldValue("dateOfPurchase", event?.toDateString() ?? "");
          }}
          invalid={!!(touched.dateOfPurchase && errors.dateOfPurchase)}
          max={todaysDate}
          errorText={errors.dateOfPurchase}
          value={formData.dateOfPurchase}
        />
        <FormInput
          id="originalPriceOfGlasses"
          labelText="Hvad var prisen oprindeligt på brillen? *"
          name="originalPriceOfGlasses"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("originalPriceOfGlasses", event.target.value);
            setFormData({
              ...formData,
              originalPriceOfGlasses: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.originalPriceOfGlasses && errors.originalPriceOfGlasses)
          }
          errorText={errors.originalPriceOfGlasses}
          value={formData.originalPriceOfGlasses}
        />
        <FormInput
          id="priceEstimate"
          labelText="Anslå prisen for at anskaffe en tilsvarende brille i dag *"
          name="priceEstimate"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("priceEstimate", event.target.value);
            setFormData({
              ...formData,
              priceEstimate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.priceEstimate && errors.priceEstimate)}
          errorText={errors.priceEstimate}
          value={formData.priceEstimate}
        />
        <FormInput
          id="consent-id"
          labelText="Jeg erklærer herved, at foranstående oplysninger er i overensstemmelse med de faktiske forhold, og at jeg vil yde selskabet enhver assistance i forbindelse med skaden. *"
          name="consent"
          type="checkbox"
          value="consent"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("consent", event.target.checked);
            setFormData({
              ...formData,
              consent: event.target.checked,
            });
          }}
          onBlur={handleBlur}
          checked={formData.consent}
        />
        {touched.consent && errors.consent && (
          <>
            <br />
            <CustomError errorText={errors.consent} />
          </>
        )}
        <Button variant={"primary"} type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default ClaimReportGlasses;
